import { Box, Fab, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";
import { translate } from "../../../../translate/translate";
import { ErrorComponent } from "../../../../components/error/error.component";
import { Forecast } from "../../../../models/general/forecast";
import TablePaginationComponent from "../../../../components/table/table-pagination.component";
import { PaginatedResult } from "../../../../models/default/paginated-result";
import { ForecastComponent } from "../../../../models/general/forecast-component";
import { useDocumentTitle } from "../../../../contexts/document-title-context";
import WarningTooltip from "../../../../components/tooltip/warning-tooltip";
import dayjs from "dayjs";
import { parseLocaleNumber } from "../../../../utils/locale-number-utils";

const ForecastItemComponent = () => {
  const navigate = useNavigate();
  const { id: forecastId } = useParams();
  const { setDocumentTitle } = useDocumentTitle();


  const service = new BaseService<Forecast>(URLS.FORECAST);

  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [searchMaterial, setSearchMaterial] = useState<string | null>(null);
  const [period, setPeriod] = useState<string | null>(null);
  const [forecastComponents, setForecastComponents] = useState<ForecastComponent[]>([]);
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const periods = [
    { value: "4", label: translate.t("4 weeks (1 month)") },
    { value: "8", label: translate.t("8 weeks (2 months)") },
    { value: "12", label: translate.t("12 weeks (3 months)") },
    { value: "16", label: translate.t("16 weeks (4 months)") },
    { value: "24", label: translate.t("24 weeks (6 months)") },
    { value: "32", label: translate.t("32 weeks (8 months)") },
    { value: "48", label: translate.t("48 weeks (12 months)") },
  ];

  const getForecastInfo = () => {
    if (forecastId == undefined) return;

    service.clearParameters();
    service.getById(forecastId)
      .then(({ data }) => {
        setForecast(data);
        setDocumentTitle(data?.customer_name);
        filter();
      })
      .catch((error) => {
        ErrorComponent(error);
      });
  };

  const filter = () => {
    if (forecastId == undefined) return;

    service.clearParameters();

    if (searchMaterial) {
      setPage(0);
      service.addParameter("part_number", searchMaterial);
    }
    if (period !== null) service.addParameter("period", period);

    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    service.getFromDetailRoutePaginate(forecastId, "components")
      .then((response) => {
        setPagination(response.data);
        setForecastComponents(response.data.results);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      });
  };

  useEffect(() => {
    getForecastInfo();
  }, []);

  useEffect(() => {
    filter();
  }, [page, rowsPerPage]);

  const { displayedColumnsShow, displayedColumns } = useMemo(() => {
    if (forecastComponents && forecastComponents.length > 0) {

      const cleanedComponents = forecastComponents.map((item: any) => {
        const cleanedItem: { [key: string]: any } = {};
        Object.keys(item).forEach(key => {
          cleanedItem[key] = item[key] === undefined || item[key] === null ? 0 : item[key];
        });
        return cleanedItem;
      });

      const firstItem = cleanedComponents[0];

      const fields = Object.keys(firstItem).filter(
        field => !["is_empty", "bom_id"].includes(field)
      );

      const convertedFields = [...fields.slice(1)];
      const formattedDates = convertedFields.map(dateStr =>
        dayjs(dateStr, "DD/MM/YYYY").format("L"),
      );

      const columns =
        fields.length > 0
          ? ["material", ...formattedDates, "total"]
          : [];

      return {
        displayedColumnsShow: columns,
        displayedColumns: [...fields, "total_forecast"],
      };
    }

    return { displayedColumnsShow: [], displayedColumns: [] };
  }, [forecastComponents, page, rowsPerPage, period]);


  const cleanedForecastComponents = useMemo(() => {
    if (!forecastComponents?.length) return [];

    return forecastComponents.map((item: any) => {
      let total = 0;
      const row: any = {};

      Object.keys(item).forEach(key => {
        if (dayjs(key, "DD/MM/YYYY", true).isValid()) {
          const qty = item[key]?.qty ?? 0;
          row[key] = qty;
          total += qty;
        } else {
          row[key] = item[key];
        }
      });

      row.total_forecast = total;

      return row;
    });
  }, [forecastComponents]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const headerCellCustomStyleHandler = (column: string) => {
    if (column === "material") {
      return {
        width: 160,
        height: 48,
        position: "sticky",
        left: 0,
        zIndex: 10,
        padding: "8px",
        fontSize: "16px",
      };
    }
    if (column === "total_forecast" || column === "total") {
      return {
        width: 160,
        minWidth: 160,
        maxWidth: 160,
        height: 48,
        position: "sticky",
        right: 0,
        zIndex: 20,
        backgroundColor: "#3949AB",
        color: "white",
        textAlign: "right",
        padding: "8px",
        boxShadow: "-4px 0 6px rgba(0,0,0,0.08)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        flexShrink: 0,
      };
    }
    return {
      width: 140,
      height: 48,
      borderWidth: "0 1px 1px 0",
      borderStyle: "solid",
      borderColor: "#e0e0e0",
      backgroundColor: "white",
      color: "#3949AB !important",
      textAlign: "right",
      padding: "8px",
      fontSize: "16px",
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cellCustomStyleHandler = (column: string, _row: any = null) => {
    if (column === "part_number") {
      return {
        position: "sticky",
        left: 0,
        zIndex: 10,
        backgroundColor: "#FAFAFA",
        textAlign: "left",
        padding: "8px",
      };
    }
    if (column === "total_forecast") {
      return {
        position: "sticky",
        right: 0,
        zIndex: 15,
        backgroundColor: "#ECEFF1",
        textAlign: "right",
        padding: "8px",
        minWidth: 100,
        maxWidth: 160,
        boxShadow: "-4px 0 6px rgba(0,0,0,0.05)",
      };
    }

    return {
      backgroundColor: "white",
      color: "#3949AB",
      textAlign: "right",
      borderWidth: "0 1px 1px 0",
      borderStyle: "solid",
      borderColor: "#e0e0e0",
      padding: "8px",
    };
  };

  const handleIconBomEmpty = (hasBomEmpty: boolean) => {
    return hasBomEmpty && <WarningTooltip messageKey={"bom_not_imported"} />;
  };

  const renderCellHandler = (column: string, index: number, row: any) => {
    const handleDoubleClick = () => {
      const newTab = window.open(`/general/bom/${row.bom_id}`, "_blank");
      if (newTab) {
        newTab.focus();
      }
    };

    if (column === "part_number") {
      return (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onDoubleClick={handleDoubleClick}
        >
          <Typography variant="body1" id="material">
            {row.part_number}
          </Typography>
          {handleIconBomEmpty(row.is_empty)}
        </Box>
      );
    }
    if (column === "total_forecast") {
      return (
        <Typography>
          {parseLocaleNumber(row.total_forecast)}
        </Typography>
      );
    }

    return parseLocaleNumber(row[column]);
  };

  const renderCellHandlers = useMemo(
    () =>
      displayedColumns.reduce(
        (acc, col) => ({ ...acc, [col]: renderCellHandler }),
        {} as Record<string, typeof renderCellHandler>,
      ),
    [displayedColumns],
  );


  return (
    <section>
      <div className="floating-title floating-title-item" style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon color="primary" fontSize="large" fontWeight="fontWeightBold" />
        </IconButton>
        <Typography variant={"h5"}>
          {forecast?.description}
        </Typography>
      </div>
      <Paper variant="outlined" sx={{ padding: "16px", marginBottom: "16px" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={9}>
            <TextField
              id="inp-forecast-material"
              label={translate.t("material")}
              fullWidth
              value={searchMaterial}
              onChange={(e) => setSearchMaterial(e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="select-period">
                {translate.t("period")}
              </InputLabel>
              <Select
                id="inp-forecast-period"
                labelId="select-period"
                value={period}
                fullWidth
                onChange={(e) => setPeriod(e.target.value)}
                label="Período"
              >
                <MenuItem value="">-</MenuItem>
                {periods.map((periodOption) => (
                  <MenuItem key={periodOption.value} value={periodOption.value}>
                    {periodOption.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md="auto">
            <Fab
              id="inp-forecast-search-submit"
              size="small"
              type="submit"
              color="primary"
              onClick={filter}
            >
              <Search />
            </Fab>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined">
        <TablePaginationComponent
          state={cleanedForecastComponents}
          displayedColumnsShow={displayedColumnsShow}
          displayedColumns={displayedColumns}
          count={pagination?.count}
          next={pagination?.next}
          previous={pagination?.previous}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          headerCellCustomStyleHandler={headerCellCustomStyleHandler}
          cellCustomStyleHandler={cellCustomStyleHandler}
          renderCellHandler={renderCellHandlers}
          stickyHeader
          disablePointer
        />
      </Paper>
    </section>
  );

};

export default ForecastItemComponent;
